import { ConfirmDialog } from "@common/ConfirmDialog";
import { ComponentColor } from "@common/types";

interface ApproveConfirmDialogProps {
    orgName: string;
    // Locations the approve will process — the org's Pending ones (#315) and
    // those waiting for payment (#434).
    pendingLocationCount: number;
    // How many of those are already waiting for payment: confirming tries
    // their charge again (review finding 8).
    pendingPaymentLocationCount: number;
    // How many of the Pending ones were already active (an admin's change of
    // state sent them back to review): the API approves them without a
    // second activation charge (C6 follow-up, PO 2026-10-28).
    activatedLocationCount: number;
    isProcessing: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

// The shared ConfirmDialog chassis (focus trap, Escape, backdrop, scroll lock,
// processing lockdown, safe-first Go back).
export const ApproveConfirmDialog = ({
    orgName,
    pendingLocationCount,
    pendingPaymentLocationCount,
    activatedLocationCount,
    isProcessing,
    onConfirm,
    onClose,
}: ApproveConfirmDialogProps) => (
    <ConfirmDialog
        title={`Approve pending locations for ${orgName}?`}
        body={
            <>
                <p>
                    {/* §1.6: a client with no card yet is approved with no
                        charge. The admin detail carries no card or membership
                        data, so the charge sentence is conditional. */}
                    {pendingLocationCount === 1
                        ? "1 pending location is"
                        : `${pendingLocationCount} pending locations are`}{" "}
                    up for approval.{" "}
                    {pendingPaymentLocationCount > 0 &&
                        (pendingPaymentLocationCount === 1
                            ? "1 of them is already waiting for payment, and its charge will be tried again. "
                            : `${pendingPaymentLocationCount} of them are already waiting for payment, and their charge will be tried again. `)}
                    {activatedLocationCount > 0 &&
                        (activatedLocationCount === 1
                            ? "1 of them was already active before it went back to review, so it is approved without a second charge. "
                            : `${activatedLocationCount} of them were already active before they went back to review, so they are approved without a second charge. `)}
                    If the organization already pays for its membership,{" "}
                    {activatedLocationCount > 0 ? "each other" : "each"}{" "}
                    location&apos;s prorated first month is charged now; a
                    location whose charge doesn&apos;t go through waits for
                    payment. Locations that have already been approved are
                    skipped and are not charged again.
                </p>
                {/* #437: the admin detail can't say which locations lack a
                    certificate, so this stays general. */}
                <p className="mt-2">
                    A pending location without a resale certificate is
                    skipped: it is not approved or charged until the customer
                    uploads one.
                </p>
            </>
        }
        confirmLabel="Approve"
        confirmColor={ComponentColor.Success}
        isProcessing={isProcessing}
        onConfirm={onConfirm}
        onClose={onClose}
    />
);
